import React, { useMemo, useState } from 'react';
import EventForm from './EventForm';
import apiService from '../services/api';
import {
  NormalizedImportEvent,
  eventDedupKey,
} from '../utils/importEventsJson';

interface ImportReviewPanelProps {
  initialItems: NormalizedImportEvent[];
  existingEvents: Array<{ name: string; city: string; date: string; link?: string }>;
  onClose: () => void;
  onImported: () => Promise<void>;
}

const ImportReviewPanel: React.FC<ImportReviewPanelProps> = ({
  initialItems,
  existingEvents,
  onClose,
  onImported,
}) => {
  const [queue, setQueue] = useState<NormalizedImportEvent[]>(initialItems);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [busyIndex, setBusyIndex] = useState<number | null>(null);
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);

  const existingKeys = useMemo(
    () =>
      new Set(
        existingEvents.map((e) =>
          eventDedupKey({
            name: e.name,
            city: e.city,
            date: e.date,
            link: e.link,
          })
        )
      ),
    [existingEvents]
  );

  const isDuplicate = (event: NormalizedImportEvent) =>
    existingKeys.has(eventDedupKey(event));

  const removeAt = (index: number) => {
    setQueue((prev) => prev.filter((_, i) => i !== index));
  };

  const acceptOne = async (index: number) => {
    const event = queue[index];
    if (!event) return;

    setBusyIndex(index);
    try {
      const result = await apiService.mergeEvents([event]);
      if (result.added > 0) {
        removeAt(index);
        await onImported();
      } else {
        alert('Pominięto — wydarzenie już jest w bazie (duplikat).');
        removeAt(index);
      }
    } catch (error) {
      alert('Błąd dodawania: ' + (error as Error).message);
    } finally {
      setBusyIndex(null);
    }
  };

  const acceptAll = async () => {
    if (queue.length === 0) return;

    const confirmed = window.confirm(
      `Dodać wszystkie pozostałe wydarzenia (${queue.length}) do bazy?\n\nDuplikaty zostaną pominięte.`
    );
    if (!confirmed) return;

    setIsAcceptingAll(true);
    try {
      const result = await apiService.mergeEvents(queue);
      alert(
        `Dodano ${result.added} wydarzeń.` +
          (result.skipped > 0 ? ` Pominięto ${result.skipped} duplikatów.` : '')
      );
      setQueue([]);
      await onImported();
    } catch (error) {
      alert('Błąd importu: ' + (error as Error).message);
    } finally {
      setIsAcceptingAll(false);
    }
  };

  const handleSaveEdit = async (updated: NormalizedImportEvent & { id?: number | string }) => {
    if (editingIndex === null) return;

    const normalized: NormalizedImportEvent = {
      name: updated.name,
      city: updated.city,
      date: updated.date,
      time: updated.time || '',
      type: updated.type || 'Koncert',
      price: updated.price || '',
      showPrice: updated.showPrice !== false,
      address: updated.address || '',
      link: updated.link || '',
      description: updated.description || '',
      multiDay: !!updated.multiDay,
      isMultiDay: !!updated.isMultiDay,
      endDate: updated.endDate || '',
      organizer: updated.organizer || '',
      phone: updated.phone || '',
      email: updated.email || '',
    };

    setQueue((prev) =>
      prev.map((item, i) => (i === editingIndex ? normalized : item))
    );
    setEditingIndex(null);
  };

  const editingEvent =
    editingIndex !== null
      ? { ...queue[editingIndex], id: `import-review-${editingIndex}` }
      : null;

  return (
    <div className="import-review-overlay">
      <div className="import-review-panel">
        <div className="import-review-header">
          <div>
            <h2>📋 Import z podglądem</h2>
            <p className="import-review-subtitle">
              Sprawdź wydarzenia przed dodaniem do bazy. Możesz edytować, odrzucić lub zaakceptować każde z osobna.
            </p>
          </div>
          <button type="button" className="import-review-close" onClick={onClose}>
            ✕ Zamknij
          </button>
        </div>

        <div className="import-review-toolbar">
          <span className="import-review-count">
            Do przeglądu: <strong>{queue.length}</strong>
          </span>
          <button
            type="button"
            className="import-review-accept-all"
            onClick={acceptAll}
            disabled={queue.length === 0 || isAcceptingAll}
          >
            {isAcceptingAll ? 'Dodawanie...' : `✅ Akceptuj wszystkie (${queue.length})`}
          </button>
        </div>

        {queue.length === 0 ? (
          <div className="import-review-empty">
            <p>Wszystkie wydarzenia z tego importu zostały przetworzone.</p>
            <button type="button" className="import-review-done-btn" onClick={onClose}>
              Zamknij panel
            </button>
          </div>
        ) : (
          <div className="import-review-list">
            {queue.map((event, index) => (
              <div
                key={`${eventDedupKey(event)}-${index}`}
                className={`import-review-card${isDuplicate(event) ? ' is-duplicate' : ''}`}
              >
                <div className="import-review-card-header">
                  <h3>{event.name}</h3>
                  {isDuplicate(event) && (
                    <span className="import-review-badge">Duplikat w bazie</span>
                  )}
                </div>
                <div className="import-review-card-body">
                  <p><strong>Miasto:</strong> {event.city}</p>
                  <p><strong>Data:</strong> {event.date}{event.time ? ` • ${event.time}` : ''}</p>
                  <p><strong>Typ:</strong> {event.type}</p>
                  {event.address && <p><strong>Adres:</strong> {event.address}</p>}
                  {event.organizer && <p><strong>Organizator:</strong> {event.organizer}</p>}
                  {event.link && (
                    <p>
                      <strong>Link:</strong>{' '}
                      <a href={event.link} target="_blank" rel="noopener noreferrer">
                        {event.link}
                      </a>
                    </p>
                  )}
                </div>
                <div className="import-review-card-actions">
                  <button
                    type="button"
                    className="import-review-edit-btn"
                    onClick={() => setEditingIndex(index)}
                    disabled={busyIndex !== null || isAcceptingAll}
                  >
                    ✏️ Edytuj
                  </button>
                  <button
                    type="button"
                    className="import-review-reject-btn"
                    onClick={() => removeAt(index)}
                    disabled={busyIndex !== null || isAcceptingAll}
                  >
                    ❌ Odrzuć
                  </button>
                  <button
                    type="button"
                    className="import-review-accept-btn"
                    onClick={() => acceptOne(index)}
                    disabled={busyIndex !== null || isAcceptingAll}
                  >
                    {busyIndex === index ? 'Dodawanie...' : '✅ Akceptuj'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingEvent && (
        <div className="modal-overlay">
          <div className="modal-content">
            <EventForm
              onAddEvent={() => {}}
              onUpdateEvent={handleSaveEdit}
              onClose={() => setEditingIndex(null)}
              editingEvent={editingEvent}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportReviewPanel;
